import { trigger, transition, style, animate } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { LineChartComponent } from '@swimlane/ngx-charts';
import { CustomTimelineChartComponent } from '../custom-timeline-chart/custom-timeline-chart.component';
import { brushX, brush } from 'd3-brush';
import { scaleLinear, scaleTime, scalePoint } from 'd3-scale';
import { select } from 'd3-selection';
import * as d3 from 'd3';

/**
 * {@inheritdoc}
 *
 * @export
 * @class CustomLineChartComponent
 * @extends {LineChartComponent}
 */
@Component({
  selector: 'app-custom-line-chart',
  template: `<ngx-charts-chart
  [view]="[width, height]"
  [showLegend]="legend"
  [legendOptions]="legendOptions"
  [activeEntries]="activeEntries"
  [animations]="animations"
  (legendLabelClick)="onClick($event)"
  (legendLabelActivate)="onActivate($event)"
  (legendLabelDeactivate)="onDeactivate($event)"
>
  <svg:defs>
    <svg:clipPath [attr.id]="clipPathId">
      <svg:rect
        [attr.width]="dims.width + 10"
        [attr.height]="dims.height + 10"
        [attr.transform]="'translate(-5, -5)'"
      />
    </svg:clipPath>
  </svg:defs>
  <svg:g [attr.transform]="transform" class="line-chart chart">
    <svg:g
      ngx-charts-x-axis
      *ngIf="xAxis"
      [xScale]="xScale"
      [dims]="dims"
      [showGridLines]="showGridLines"
      [showLabel]="showXAxisLabel"
      [labelText]="xAxisLabel"
      [trimTicks]="trimXAxisTicks"
      [rotateTicks]="rotateXAxisTicks"
      [maxTickLength]="maxXAxisTickLength"
      [tickFormatting]="xAxisTickFormatting"
      [ticks]="xAxisTicks"
      (dimensionsChanged)="updateXAxisHeight($event)"
    ></svg:g>
    <svg:g
      ngx-charts-y-axis
      *ngIf="yAxis"
      [yScale]="yScale"
      [dims]="dims"
      [showGridLines]="showGridLines"
      [showLabel]="showYAxisLabel"
      [labelText]="yAxisLabel"
      [trimTicks]="trimYAxisTicks"
      [maxTickLength]="maxYAxisTickLength"
      [tickFormatting]="yAxisTickFormatting"
      [ticks]="yAxisTicks"
      [referenceLines]="referenceLines"
      [showRefLines]="showRefLines"
      [showRefLabels]="showRefLabels"
      (dimensionsChanged)="updateYAxisWidth($event)"
    ></svg:g>
    <svg:g [attr.clip-path]="clipPath">
      <svg:g *ngFor="let series of results; trackBy: trackBy" [@animationState]="'active'">
        <svg:g
          ngx-charts-line-series
          [xScale]="xScale"
          [yScale]="yScale"
          [colors]="colors"
          [data]="series"
          [activeEntries]="activeEntries"
          [scaleType]="scaleType"
          [curve]="curve"
          [rangeFillOpacity]="rangeFillOpacity"
          [hasRange]="hasRange"
          [animations]="animations"
        />
      </svg:g>
      <svg:g *ngIf="!tooltipDisabled" (mouseleave)="hideCircles()">
        <svg:g
          ngx-charts-tooltip-area
          [dims]="dims"
          [xSet]="xSet"
          [xScale]="xScale"
          [yScale]="yScale"
          [results]="results"
          [colors]="colors"
          [tooltipDisabled]="tooltipDisabled"
          [tooltipTemplate]="seriesTooltipTemplate"
          (hover)="updateHoveredVertical($event)"
        />
        <svg:g *ngFor="let series of results">
          <svg:g
            ngx-charts-circle-series
            [xScale]="xScale"
            [yScale]="yScale"
            [colors]="colors"
            [data]="series"
            [scaleType]="scaleType"
            [visibleValue]="hoveredVertical"
            [activeEntries]="activeEntries"
            [tooltipDisabled]="tooltipDisabled"
            [tooltipTemplate]="tooltipTemplate"
            (select)="onClick($event)"
            (activate)="onActivate($event)"
            (deactivate)="onDeactivate($event)"
          />
        </svg:g>
      </svg:g>
    </svg:g>
  </svg:g>
  <svg:g
    class="timeline-brush"
    app-custom-timeline-chart
    *ngIf="timeline && scaleType != 'ordinal'"
    [attr.transform]="timelineTransform"
    [results]="results"
    [view]="[timelineWidth, height]"
    [height]="timelineHeight"
    [filteredDomain]="customFilteredDomain"
    [scheme]="scheme"
    [customColors]="customColors"
    [scaleType]="scaleType"
    [legend]="legend"
    (onDomainChange)="updateDomain($event)"
  >
    <svg:g *ngFor="let series of results; trackBy: trackBy">
      <svg:g
        ngx-charts-line-series
        [xScale]="timelineXScale"
        [yScale]="timelineYScale"
        [colors]="colors"
        [data]="series"
        [scaleType]="scaleType"
        [curve]="curve"
        [hasRange]="hasRange"
        [animations]="animations"
      />
    </svg:g>
  </svg:g>
</ngx-charts-chart>`,
  styleUrls: ['../../../../../node_modules/@swimlane/ngx-charts/release/common/base-chart.component.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('animationState', [
      transition(':leave', [
        style({
          opacity: 1
        }),
        animate(
          500,
          style({
            opacity: 0
          })
        )
      ])
    ])
  ]
})
export class CustomLineChartComponent extends  LineChartComponent {
  @ViewChild('.timeline-brush', {static:true, read:true}) timeline_ref :CustomTimelineChartComponent ;
  customFilteredDomain;
  constructor(chartElement: ElementRef, zone: NgZone, cd: ChangeDetectorRef) {
    super(chartElement, zone, cd);
   }

   /**
    * Method for set the filtered domain using dates in this chart.
    *
    * @param {*} a The first value.
    * @param {*} b The last value.
    * @memberof CustomLineChartComponent
    */
   setFilteredDomain(a,b){
    this.filteredDomain = [a,b];
    this.customFilteredDomain = [a,b];
    this.timelineXScale();
    this.update();
  }

   /**
    * Method for clear the filtered domain.
    *
    * @memberof CustomLineChartComponent
    */
   clearFilteredDomain(){
     this.filteredDomain = null;
     this.update();
   }

   /**
    * New method for get the YScale of Y domain for timeline.
    *
    * @param {*} domain
    * @param {*} height
    * @returns {*}
    * @memberof CustomLineChartComponent
    */
   getYScaleForTimeLine(domain, height): any {
    let maxValue = 100;
    if(this.results && this.results[0] && this.results[0]['series']){
      // Map the numeric values.
      const data =(this.results[0]['series'].map(value=> value.value));
      // Reduce the array to find the max number.
      maxValue =  data.reduce(function (p, v) {
        return ( p > v ? p : v );
      });
    }
    return super.getYScale([maxValue, 1], height);
  }

  /**
   * Override of update timeline for get the feature requested at https://app.asana.com/0/1156796691380146/1199719364935717.
   *
   * @memberof CustomLineChartComponent
   */
  updateTimeline(): void {
    if (this.timeline) {
      this.timelineXDomain = undefined;
      this.timelineWidth = this.dims.width;
      this.timelineXDomain = this.getXDomain();
      this.timelineXScale = this.getXScale(this.timelineXDomain, this.timelineWidth);
      // call our custom method.
      this.timelineYScale = this.getYScaleForTimeLine(this.yDomain, this.timelineHeight);
      this.timelineTransform = `translate(${this.dims.xOffset}, ${-this.margin[2]})`;
    }
  }
}
